import React, { useState } from 'react'
import Button from '../components/ui/button'
import Loader from '../components/ui/loader.jsx'
import Validator from '../utils/validator'
import apiService from '../services/api.service'
import { useTenant } from '../context/TenantContext'
import { useLanguage } from '../context/LanguageContext'

const MigrationRequest = () => {
  const { tenant } = useTenant()
  const { languageId, t } = useLanguage()

  const [formData, setFormData] = useState({ email: '' })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Validate email
    if (!formData.email) {
      newErrors.email = t('accountMigration.errors.emailRequired')
    } else if (!Validator.isValidEmail(formData.email)) {
      newErrors.email = t('accountMigration.errors.emailInvalid')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      const response = await apiService.post('/api/kc/initiate-migration', {
        clientemail: formData.email,
        clienttenantid: tenant?.tenantid || 1011,
        defaultlanguageid: languageId || 490,
      })

      // Success - show success state
      setIsSubmitted(true)
    } catch (error) {
      console.error('Migration request error:', error)

      // Handle API errors - apiService.handleError transforms to { message, status, data, isRateLimit }
      if (error.isRateLimit) {
        setErrors({ submit: t('accountMigration.errors.rateLimitExceeded') })
      } else if (error.data?.message) {
        setErrors({ submit: error.data.message })
      } else {
        setErrors({
          submit: t('accountMigration.errors.migrationFailed'),
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {isLoading && (
        <Loader
          fullscreen
          label={t('accountMigration.loadingLabel')}
          size="xl"
        />
      )}

      <div className="w-full px-6 mt-10 md:max-w-[600px] md:mx-auto">
        <div className="w-full px-6 md:max-w-[600px] md:mx-auto">
          {/* Migration Request - START */}
          {!isSubmitted ? (
            <form
              className="form-container"
              onSubmit={handleSubmit}
              noValidate
              aria-label="Migration Request form"
            >
              <div className="text-center mb-4">
                <h1 className="text-base font-extrabold text-theme-secondary">
                  {t('accountMigration.title')}
                </h1>
                <p className="text-xs text-theme-secondary mt-2">
                  {t('accountMigration.subtitle')}
                </p>
              </div>

              {/* Display general error */}
              {errors.submit && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{errors.submit}</p>
                </div>
              )}

              <div className="space-y-4">
                <div className="grid gap-1">
                  <label
                    htmlFor="email"
                    className="text-sm font-normal text-theme-accent text-left"
                  >
                    {t('accountMigration.emailLabel')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`mt-1 w-full h-12 appearance-none rounded-md border ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    } bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-theme-primary`}
                    placeholder={t('accountMigration.emailPlaceholder')}
                    autoComplete="email"
                    aria-required="true"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  {errors.email && (
                    <p id="email-error" className="text-xs text-red-500 mt-1">
                      {errors.email}
                    </p>
                  )}
                </div>

                <Button
                  id="migration-btn"
                  variant="primary"
                  size="lg"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading
                    ? t('accountMigration.sendingButton')
                    : t('accountMigration.submitButton')}
                </Button>
              </div>
            </form>
          ) : (
            /* Success State */
            <div className="text-center">
              <div className="mb-6">
                <svg
                  className="mx-auto h-16 w-16 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-theme-secondary mb-2">
                {t('accountMigration.successTitle')}
              </h2>
              <p className="text-sm text-theme-secondary mb-4">
                {t('accountMigration.successMessage')
                  .split('{email}')
                  .map((part, index, array) =>
                    index < array.length - 1 ? (
                      <React.Fragment key={index}>
                        {part}
                        <strong>{formData.email}</strong>
                      </React.Fragment>
                    ) : (
                      part
                    ),
                  )}
              </p>
              <p className="text-xs text-gray-500 mb-6">
                {t('accountMigration.successNote')}
              </p>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsSubmitted(false)
                  setFormData({ email: '' })
                  setErrors({})
                }}
              >
                {t('accountMigration.sendAnother')}
              </Button>
            </div>
          )}
          {/* Migration Request - END */}
        </div>
      </div>
    </>
  )
}

export default MigrationRequest
